/**
 * Enhanced Service Attendee Report Component
 * Features: Column filters, sticky headers, drag/drop columns, filtered PDF export
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FunnelIcon,
  DocumentArrowDownIcon,
  ArrowsUpDownIcon,
  XMarkIcon,
  EyeSlashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const EnhancedServiceAttendeeReport = ({
  data,
  onExport,
  className = ''
}) => {
  // State for filters
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [columnOrder, setColumnOrder] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Refs
  const tableRef = useRef();

  // Initialize column order from data
  useEffect(() => {
    if (data?.columns && columnOrder.length === 0) {
      setColumnOrder(data.columns.map(col => col.key));
    }
  }, [data?.columns, columnOrder.length]);

  // Filter and sort data
  const processedData = useMemo(() => {
    if (!data?.data) return [];

    let filtered = data.data.filter(row => {
      return Object.entries(columnFilters).every(([columnKey, filterValue]) => {
        if (!filterValue || filterValue.trim() === '') return true;

        const cellValue = String(row[columnKey] || '').toLowerCase();
        const filter = filterValue.toLowerCase();

        return cellValue.includes(filter);
      });
    });

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        const aStr = String(aVal).toLowerCase();
        const bStr = String(bVal).toLowerCase();

        if (sortConfig.direction === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      });
    }

    return filtered;
  }, [data?.data, columnFilters, sortConfig]);

  // Update filtered data when processed data changes
  useEffect(() => {
    setFilteredData(processedData);
  }, [processedData]);

  // Handle column filter change
  const handleFilterChange = (columnKey, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  // Handle sort
  const handleSort = (columnKey) => {
    setSortConfig(prev => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle column visibility toggle
  const toggleColumnVisibility = (columnKey) => {
    setHiddenColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  // Handle drag end for column reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newColumnOrder = Array.from(columnOrder);
    const [reorderedItem] = newColumnOrder.splice(result.source.index, 1);
    newColumnOrder.splice(result.destination.index, 0, reorderedItem);

    setColumnOrder(newColumnOrder);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setColumnFilters({});
  };

  // Export filtered data to PDF
  const exportFilteredToPDF = () => {
    try {
      // Create a simple PDF without autoTable if it's not available
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

      // Get visible columns in order
      const visibleColumns = columnOrder
        .filter(key => !hiddenColumns.has(key))
        .map(key => data.columns.find(col => col.key === key))
        .filter(Boolean);

      // Check if autoTable is available
      if (typeof doc.autoTable === 'function') {
        // Use autoTable if available
        generateAutoTablePDF(doc, visibleColumns);
      } else {
        // Manual PDF generation fallback
        generateManualPDF(doc, visibleColumns);
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF. Please try again or contact support.');
    }
  };

  const generateAutoTablePDF = (doc, visibleColumns) => {
    // Prepare table data with better value handling
    const headers = visibleColumns.map(col => col.label);
    const rows = filteredData.map(row =>
      visibleColumns.map(col => {
        const value = row[col.key];

        // Handle different data types properly
        if (value === null || value === undefined) {
          return '';
        }

        if (col.format === 'currency' && typeof value === 'number') {
          return `$${value.toFixed(2)}`;
        }

        // Ensure all values are converted to strings and handle nested objects
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }

        return String(value);
      })
    );

    // Add title
    doc.setFontSize(16);
    doc.text('Service Attendee Report (Filtered)', 14, 15);

    // Add filter summary if any filters are active
    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value?.trim());
    let yPos;
    if (activeFilters.length > 0) {
      doc.setFontSize(10);
      yPos = 25;
      doc.text('Active Filters:', 14, yPos);
      activeFilters.forEach(([column, filter]) => {
        yPos += 5;
        const columnLabel = data.columns.find(col => col.key === column)?.label || column;
        doc.text(`• ${columnLabel}: ${filter}`, 20, yPos);
      });
      yPos += 10;
    } else {
      yPos = 30;
    }

    // Calculate column widths based on content
    const columnWidths = visibleColumns.map(col => {
      // Special handling for common column types
      if (col.key.includes('phone') || col.key.includes('Phone')) {
        return 25; // Phone numbers
      }
      if (col.key.includes('email') || col.key.includes('Email')) {
        return 35; // Email addresses
      }
      if (col.key.includes('reservationId') || col.key.includes('Res')) {
        return 20; // Reservation IDs
      }
      if (col.key.includes('amount') || col.key.includes('Amount')) {
        return 15; // Currency amounts
      }

      // Base width on header length with reasonable limits
      const headerLength = col.label.length;
      return Math.max(15, Math.min(40, headerLength * 1.2));
    });

    // Add table using autoTable with better configuration
    doc.autoTable({
      head: [headers],
      body: rows,
      startY: yPos,
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8
      },
      columnStyles: {
        // Configure each column individually
        ...visibleColumns.reduce((acc, col, index) => {
          const baseStyle = {
            cellWidth: columnWidths[index],
            overflow: 'linebreak'
          };

          if (col.format === 'currency') {
            baseStyle.halign = 'right';
          }

          acc[index] = baseStyle;
          return acc;
        }, {})
      },
      margin: { top: 10, left: 14, right: 14 },
      tableWidth: 'auto',
      theme: 'grid'
    });

    // Add summary
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text(`Total Records: ${filteredData.length}`, 14, finalY);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, finalY + 5);

    // Save the PDF
    doc.save(`service-attendee-report-filtered-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const generateManualPDF = (doc, visibleColumns) => {
    // Manual table generation without autoTable
    doc.setFontSize(16);
    doc.text('Service Attendee Report (Filtered)', 14, 15);

    // Add filter summary if any filters are active
    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value?.trim());
    let yPos = 30;
    if (activeFilters.length > 0) {
      doc.setFontSize(10);
      doc.text('Active Filters:', 14, yPos);
      yPos += 5;
      activeFilters.forEach(([column, filter]) => {
        const columnLabel = data.columns.find(col => col.key === column)?.label || column;
        doc.text(`• ${columnLabel}: ${filter}`, 20, yPos);
        yPos += 5;
      });
      yPos += 5;
    }

    // Calculate dynamic column widths based on content
    const pageWidth = 297 - 28; // Landscape A4 width minus margins
    const minColWidth = 20; // Minimum column width
    const maxColWidth = 50; // Maximum column width for long headers

    // Calculate column widths based on header length and content type
    const columnWidths = visibleColumns.map(col => {
      const headerLength = col.label.length;

      // Special handling for common column types
      if (col.key.includes('phone') || col.key.includes('Phone')) {
        return 30; // Phone numbers need consistent width
      }
      if (col.key.includes('email') || col.key.includes('Email')) {
        return 40; // Email addresses need more space
      }
      if (col.key.includes('reservationId') || col.key.includes('Res')) {
        return 25; // Reservation IDs are typically short
      }
      if (col.key.includes('amount') || col.key.includes('Amount')) {
        return 20; // Amounts are typically short numbers
      }
      if (col.key.includes('participantCount') || col.key.includes('Participants')) {
        return 15; // Participant count is just a number
      }

      // For other columns, use header length as guide
      let width = Math.max(minColWidth, Math.min(maxColWidth, headerLength * 1.8));
      return width;
    });

    // Normalize widths to fit page
    const totalCalculatedWidth = columnWidths.reduce((sum, width) => sum + width, 0);
    const scaleFactor = pageWidth / totalCalculatedWidth;
    const finalColumnWidths = columnWidths.map(width => width * scaleFactor);

    // Headers with word wrapping
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    let xPos = 14;
    let maxHeaderHeight = 0;

    visibleColumns.forEach((col, index) => {
      const colWidth = finalColumnWidths[index];
      const lines = doc.splitTextToSize(col.label, colWidth - 2);

      // Draw each line of the header
      lines.forEach((line, lineIndex) => {
        doc.text(line, xPos, yPos + (lineIndex * 4));
      });

      maxHeaderHeight = Math.max(maxHeaderHeight, lines.length * 4);
      xPos += colWidth;
    });

    yPos += maxHeaderHeight + 4;

    // Data rows with proper wrapping
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);

    filteredData.forEach((row, rowIndex) => {
      let maxRowHeight = 0;
      let rowLines = [];

      // First pass: calculate how many lines each cell needs
      visibleColumns.forEach((col, colIndex) => {
        const colWidth = finalColumnWidths[colIndex];
        const value = row[col.key];
        let displayValue = '';

        // Handle different data types properly
        if (value === null || value === undefined) {
          displayValue = '';
        } else if (col.format === 'currency' && typeof value === 'number') {
          displayValue = `$${value.toFixed(2)}`;
        } else if (typeof value === 'object') {
          displayValue = JSON.stringify(value);
        } else {
          displayValue = String(value);
        }

        // Split text to fit column width
        const lines = doc.splitTextToSize(displayValue, colWidth - 2);
        rowLines[colIndex] = lines;
        maxRowHeight = Math.max(maxRowHeight, lines.length);
      });

      // Check if we need a new page
      const requiredHeight = maxRowHeight * 4 + 2;
      if (yPos + requiredHeight > 280) { // Leave space at bottom
        doc.addPage();
        yPos = 20;
      }

      // Second pass: draw all the lines for this row
      for (let lineIndex = 0; lineIndex < maxRowHeight; lineIndex++) {
        let xPos = 14;
        visibleColumns.forEach((col, colIndex) => {
          const colWidth = finalColumnWidths[colIndex];
          const lines = rowLines[colIndex];

          if (lines && lines[lineIndex]) {
            doc.text(lines[lineIndex], xPos, yPos + (lineIndex * 4));
          }

          xPos += colWidth;
        });
      }

      yPos += requiredHeight;
    });

    // Add summary
    yPos += 10;
    doc.setFontSize(10);
    doc.text(`Total Records: ${filteredData.length}`, 14, yPos);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos + 5);

    // Save the PDF
    doc.save(`service-attendee-report-filtered-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Format cell value
  const formatCellValue = (value, column) => {
    if (value === null || value === undefined) return '';

    if (column.format === 'currency' && typeof value === 'number') {
      return `$${value.toFixed(2)}`;
    }

    return String(value);
  };

  if (!data || !data.columns || !data.data) {
    return (
      <div className="text-center py-8 text-gray-500">
        No data available for Service Attendee Report
      </div>
    );
  }

  const activeFilterCount = Object.values(columnFilters).filter(v => v?.trim()).length;
  const visibleColumns = columnOrder
    .filter(key => !hiddenColumns.has(key))
    .map(key => data.columns.find(col => col.key === key))
    .filter(Boolean);

  return (
    <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
      {/* Header Controls */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Service Attendee Report
            </h3>
            <span className="text-sm text-gray-600">
              {filteredData.length} of {data.data.length} records
            </span>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            )}

            {/* Export PDF */}
            <button
              onClick={exportFilteredToPDF}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
              Export Filtered PDF
            </button>
          </div>
        </div>

        {/* Column Visibility Controls */}
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-sm font-medium text-gray-700">Show/Hide Columns:</span>
          {data.columns.map(column => (
            <button
              key={column.key}
              onClick={() => toggleColumnVisibility(column.key)}
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                hiddenColumns.has(column.key)
                  ? 'bg-gray-200 text-gray-600'
                  : 'bg-blue-100 text-blue-800'
              }`}
            >
              {hiddenColumns.has(column.key) ? (
                <EyeSlashIcon className="h-3 w-3 mr-1" />
              ) : (
                <EyeIcon className="h-3 w-3 mr-1" />
              )}
              {column.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table Container with Sticky Headers */}
      <div className="overflow-auto max-h-[70vh]" ref={tableRef}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <table className="min-w-full divide-y divide-gray-200">
            {/* Sticky Header */}
            <Droppable droppableId="columns" direction="horizontal">
              {(provided) => (
                <thead
                  className="bg-gray-50 sticky top-0 z-10"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <tr>
                    {visibleColumns.map((column, index) => (
                      <Draggable
                        key={column.key}
                        draggableId={column.key}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <th
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 border-r border-gray-200 ${
                              snapshot.isDragging ? 'shadow-lg bg-blue-50' : ''
                            }`}
                            style={{
                              ...provided.draggableProps.style,
                              width: column.width || 'auto',
                              minWidth: '120px'
                            }}
                          >
                            <div className="space-y-2">
                              {/* Column Header with Sort and Drag Handle */}
                              <div className="flex items-center justify-between">
                                <button
                                  onClick={() => handleSort(column.key)}
                                  className="flex items-center space-x-1 hover:text-gray-700 flex-1"
                                >
                                  <span>{column.label}</span>
                                  {sortConfig.key === column.key && (
                                    <ArrowsUpDownIcon className={`h-4 w-4 ${
                                      sortConfig.direction === 'asc' ? 'transform rotate-180' : ''
                                    }`} />
                                  )}
                                </button>
                                <div
                                  {...provided.dragHandleProps}
                                  className="cursor-move p-1 hover:bg-gray-200 rounded"
                                >
                                  <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" />
                                </div>
                              </div>

                              {/* Filter Input */}
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder="Filter..."
                                  value={columnFilters[column.key] || ''}
                                  onChange={(e) => handleFilterChange(column.key, e.target.value)}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <FunnelIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                              </div>
                            </div>
                          </th>
                        )}
                      </Draggable>
                    ))}
                  </tr>
                  {provided.placeholder}
                </thead>
              )}
            </Droppable>

            {/* Table Body */}
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50">
                  {visibleColumns.map((column) => (
                    <td
                      key={column.key}
                      className="px-4 py-3 text-sm text-gray-900 border-r border-gray-100"
                      style={{ width: column.width || 'auto' }}
                    >
                      {formatCellValue(row[column.key], column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </DragDropContext>
      </div>

      {/* Footer with Summary */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center text-sm text-gray-600">
          <div>
            Showing {filteredData.length} of {data.data.length} total records
          </div>
          <div>
            {data.metadata && (
              <span>
                {data.metadata.totalAddons || 0} add-on columns, {' '}
                {data.metadata.totalQuestions || 0} questionnaire columns
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedServiceAttendeeReport;